import React from 'react';
import Icon from '../shared/Icon';
import { isAwaitingReviewOnly } from '../../../utils/datasetReviewState';

/**
 * PRD-1359 — the dataset action list, shared by the card's overflow menu and the
 * Dataset details modal's.
 *
 * The feedback doc asks the modal to "match the options to the menu at the card
 * level". Two hand-maintained copies of that list is exactly how they stop
 * matching, so this is the one list and both surfaces render it. Only the shell
 * differs — the card has its own dropdown, the modal uses RowActionsMenu.
 *
 * Emits bare <button>s with no wrapper, so each shell keeps its own styling.
 * Every handler is optional: a surface that cannot perform an action simply
 * doesn't pass it, and that item disappears rather than rendering dead.
 *
 * @param {() => void} [close] the shell's dismiss callback, invoked before each
 *   action so the menu never lingers over whatever the action opens.
 */
/**
 * Whether a dataset action menu should be rendered at all.
 *
 * Lives beside the item list so the two cannot drift: this answers "would that
 * list produce anything the viewer is allowed to use", and it is the shells'
 * job to ask before mounting a trigger.
 *
 * Two independent reasons to say no, and each was a real defect:
 *
 *  - NEITHER CAPABILITY. DatasetMenuItems would render zero children, but
 *    RowActionsMenu still renders its trigger and portals its dropdown to
 *    document.body — so a viewer-role user clicked the kebab and got an empty
 *    white box floating over the page.
 *
 *  - NOT THE OWNER. Every action below is owner-only server-side, so a dataset
 *    shared TO someone offered them Share / Edit / Pause / Delete that the API
 *    then refuses; Pause round-trips into a "Failed to pause dataset" toast.
 *
 * `is_owner !== false`, not `=== true`: the field is absent from some list
 * responses, and treating absent as "not owner" would strip the menu from
 * datasets the user does own.
 */
export const canShowDatasetMenu = (dataset, features = {}) => Boolean(
  // Guard the dataset first. `dataset?.is_owner !== false` is TRUE for a null
  // dataset — undefined !== false — so without this a menu renders for nothing
  // at all. Today's call sites happen to guard it themselves; this is a
  // predicate, so it should not depend on that.
  dataset
  && (features?.can_edit_dataset || features?.can_delete_dataset)
  && dataset.is_owner !== false,
);

const DatasetMenuItems = ({
  dataset,
  features = {},
  close,
  onReviewFields,
  onToggleShare,
  onEdit,
  onTogglePause,
  onDelete,
  deleteClassName = 'data-sources-menu-delete',
}) => {
  // Each item stops propagation because the card menu sits inside a clickable
  // card — without it, choosing an action also opens the dataset.
  const act = (fn) => (e) => {
    e.stopPropagation();
    if (close) close();
    fn(dataset);
  };

  // Pause is only relevant while ingestion is in flight. Resume always shows for
  // a paused dataset so the user can lift the pause regardless of where it sat.
  const job = (dataset?.job_status || '').toLowerCase();
  const isInFlight = job === 'processing' || job === 'queued' || job === 'awaiting_input';
  const isPaused = Boolean(dataset?.paused_at);
  const showPause = isPaused || isInFlight;

  return (
    <>
      {/* Top of the menu: the only time-sensitive action here. */}
      {onReviewFields && isAwaitingReviewOnly(dataset) && (
        <button onClick={act(onReviewFields)}>
          <Icon name="edit_note" /> Review fields
        </button>
      )}
      {features.can_edit_dataset && onToggleShare && (
        <button onClick={act(onToggleShare)}>
          <Icon name={dataset?.visibility === 'organization' ? 'lock' : 'groups'} />
          {dataset?.visibility === 'organization' ? ' Make private' : ' Share'}
        </button>
      )}
      {features.can_edit_dataset && onEdit && (
        <button onClick={act(onEdit)}>
          <Icon name="edit" /> Edit
        </button>
      )}
      {features.can_edit_dataset && onTogglePause && showPause && (
        <button onClick={act(onTogglePause)}>
          <Icon name={isPaused ? 'play_arrow' : 'pause'} />
          {isPaused ? ' Resume' : ' Pause'}
        </button>
      )}
      {features.can_delete_dataset && onDelete && (
        <button className={deleteClassName} onClick={act(onDelete)}>
          <Icon name="delete" /> Delete
        </button>
      )}
    </>
  );
};

export default DatasetMenuItems;
